import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import styled, { css } from 'styled-components'
import { Styles as S } from '~/pages/anatomy/libs/singleChoiceStyles'
import SectionProgress from '~/pages/anatomyV2/libs/components/SectionProgress'
import { ANATOMY_V2_ACCENT_COLOR } from '~/pages/anatomyV2/libs/constants'
import { Cohort, OptionType } from '~/shared/constants/commonConstants'
import { useDelayedContinue } from '~/shared/hooks/useDelayedContinue'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { selectCohortID } from '~/shared/store/common/selects'
import { TPageProps } from '~/shared/types/commonTypes'

const SECTION_NUMBER = 6

const OptionsAccentWrapper = styled.div<{ $isAnatomyV2: boolean }>`
  display: contents;

  ${({ $isAnatomyV2 }) =>
    $isAnatomyV2 &&
    css`
      label:has(input:checked) {
        background: #ede7fe;
        border-color: ${ANATOMY_V2_ACCENT_COLOR} !important;
      }
    `}
`

const OPTION_KEYS = [
  { key: 'casual', i18nLabel: 'first', i18nAdditional: 'firstAdditional' },
  { key: 'regular', i18nLabel: 'second', i18nAdditional: 'secondAdditional' },
  { key: 'serious', i18nLabel: 'third', i18nAdditional: 'thirdAdditional' },
  {
    key: 'determined',
    i18nLabel: 'fourth',
    i18nAdditional: 'fourthAdditional',
  },
] as const

const DailyGoalAnatomy = ({ pageId, nextPagePath }: TPageProps) => {
  const { t } = useTranslation()
  const cohort = useSelector(selectCohortID)
  const isAnatomyV2 = cohort === Cohort.ANATOMY_V2
  const [answer, setAnswer] = useState('')
  const { isAnswersDisabled, handleChange, setIsAnswersDisabled } =
    useDelayedContinue()

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: `Let's set your daily goal`,
  })

  return (
    <S.Root>
      <S.StyledContainer $isAnatomyV2={isAnatomyV2}>
        {isAnatomyV2 && (
          <SectionProgress currentSection={SECTION_NUMBER} pageId={pageId} />
        )}

        <div>
          <S.Question $isAnatomyV2={isAnatomyV2}>
            <Trans i18nKey="onboarding.quizSingleChoice.dailyGoal.title" />
          </S.Question>

          <S.Subtitle $isTextLeft>
            <Trans i18nKey="onboarding.quizSingleChoice.dailyGoal.subtitle" />
          </S.Subtitle>
        </div>

        <OptionsAccentWrapper $isAnatomyV2={isAnatomyV2}>
          <S.OptionsBottomWrapper>
            {OPTION_KEYS.map(({ key, i18nLabel, i18nAdditional }, index) => (
              <S.StyledOption
                $withAdditionalValue
                key={key}
                $index={index}
                type={OptionType.RADIO}
                value={key}
                name="dailyGoal"
                checked={answer === key}
                disabled={isAnswersDisabled}
                onChange={(optionValue) => {
                  setAnswer(optionValue)
                  handleChange(optionValue, handleContinue)
                  setIsAnswersDisabled(true)
                }}
              >
                <S.Value>
                  {t(
                    `onboarding.quizSingleChoice.dailyGoal.options.${i18nLabel}`,
                  )}
                </S.Value>

                <S.AdditionalValue>
                  {t(
                    `onboarding.quizSingleChoice.dailyGoal.options.${i18nAdditional}`,
                  )}
                </S.AdditionalValue>
              </S.StyledOption>
            ))}
          </S.OptionsBottomWrapper>
        </OptionsAccentWrapper>
      </S.StyledContainer>
    </S.Root>
  )
}

export default DailyGoalAnatomy
